"use client"
import { useLingui } from "@lingui/react/macro"
import { useLinkAccount, useUser } from "@privy-io/react-auth"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { wrap } from "@reatom/core"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { z } from "zod"
import {
  accountSettingsRequest,
  consentStatusSchema,
} from "./account-settings-api"
import { useAccountEmailAuth } from "./use-account-email-auth"
import { notificationPreferenceQueryKey } from "./use-notification-preferences"

type BindingOperation = { accountId: string; intentId: string }
type BindingState = {
  accountId: string | null
  pending: boolean
  error: string | null
  completed: boolean
}

/** Shared first-email consent flow. Caller displays consent before beginBinding(). */
export function useEmailBinding() {
  const { t } = useLingui()
  const { userId, authenticated, getAccessToken, bffBaseUrl } =
    useAccountEmailAuth()
  const { refreshUser } = useUser()
  const client = useQueryClient()
  const currentAccount = useRef(userId)
  currentAccount.current = userId
  const operation = useRef<BindingOperation | null>(null)
  const active = useRef(false)
  const [state, setState] = useState<BindingState>({
    accountId: userId,
    pending: false,
    error: null,
    completed: false,
  })
  useEffect(() => {
    currentAccount.current = userId
    operation.current = null
    active.current = false
    return () => {
      currentAccount.current = null
      operation.current = null
      active.current = false
    }
  }, [userId])
  const queryKey = ["email-consent", userId]
  function updateState(accountId: string, change: Partial<BindingState>) {
    if (currentAccount.current !== accountId) return
    setState((previous) => ({ ...previous, ...change, accountId }))
  }
  async function apiRequest(
    accountId: string,
    method: "GET" | "POST" | "PUT" = "GET",
    body?: { consent: true } | { intentId: string },
  ) {
    if (currentAccount.current !== accountId)
      throw new Error("Your Account changed. Please try again.")
    return wrap(
      accountSettingsRequest(
        { bffBaseUrl, accountId, getAccessToken },
        "email-consent",
        method,
        body,
      ),
    )
  }
  const status = useQuery({
    queryKey,
    queryFn: async () =>
      consentStatusSchema.parse(await wrap(apiRequest(userId ?? ""))),
    enabled: authenticated && Boolean(userId),
    retry: false,
  })
  async function finish(pending: BindingOperation) {
    active.current = true
    updateState(pending.accountId, {
      pending: true,
      error: null,
      completed: false,
    })
    try {
      const refreshed = await wrap(refreshUser())
      if (
        refreshed.id !== pending.accountId ||
        currentAccount.current !== pending.accountId
      )
        return
      z.object({ completed: z.literal(true) }).parse(
        await wrap(
          apiRequest(pending.accountId, "PUT", { intentId: pending.intentId }),
        ),
      )
      if (currentAccount.current !== pending.accountId) return
      operation.current = null
      active.current = false
      updateState(pending.accountId, {
        pending: false,
        error: null,
        completed: true,
      })
      client.setQueryData(["email-consent", pending.accountId], {
        pendingIntentId: null,
        emailConnected: true,
      })
      await wrap(
        client.invalidateQueries({
          queryKey: notificationPreferenceQueryKey(pending.accountId),
        }),
      )
    } catch (error) {
      active.current = false
      updateState(pending.accountId, {
        pending: false,
        error: `Your email consent has not been confirmed. ${error instanceof Error ? error.message : "Please try again."}`,
      })
      void client.invalidateQueries({
        queryKey: ["email-consent", pending.accountId],
      })
    }
  }
  const { linkEmail } = useLinkAccount({
    onSuccess: ({ user, linkMethod }) => {
      const pending = operation.current
      if (
        linkMethod !== "email" ||
        !pending ||
        user.id !== pending.accountId ||
        currentAccount.current !== pending.accountId
      )
        return
      void wrap(finish(pending))
    },
    onError: (error, details) => {
      const pending = operation.current
      if (
        details.linkMethod !== "email" ||
        !pending ||
        currentAccount.current !== pending.accountId
      )
        return
      operation.current = null
      active.current = false
      const message = String(error)
      if (/exited_link_flow|cancel|closed|reject/i.test(message)) {
        updateState(pending.accountId, { pending: false, error: null })
        sonnerToast.error(t`Email verification failed.`)
        return
      }
      updateState(pending.accountId, {
        pending: false,
        error: /already|linked|exists/i.test(message)
          ? "This email is already linked to another Account. Use a different email."
          : `Email verification failed. ${message}`,
      })
    },
  })
  const start = useMutation({
    mutationFn: async (accountId: string) => {
      updateState(accountId, { pending: true, error: null, completed: false })
      try {
        const refreshed = await wrap(refreshUser())
        if (refreshed.id !== accountId || currentAccount.current !== accountId)
          return
        if (refreshed.email?.address)
          throw new Error(
            "Your Account already has email. Use Change to update it.",
          )
        const { intentId } = z
          .object({ intentId: z.string() })
          .parse(await wrap(apiRequest(accountId, "POST", { consent: true })))
        if (currentAccount.current !== accountId) return
        operation.current = { accountId, intentId }
        client.setQueryData(["email-consent", accountId], {
          pendingIntentId: intentId,
          emailConnected: false,
        })
        linkEmail()
      } catch (error) {
        operation.current = null
        active.current = false
        updateState(accountId, {
          pending: false,
          error:
            error instanceof Error
              ? error.message
              : "Unable to start email verification.",
        })
      }
    },
  })
  const relevant =
    state.accountId === userId
      ? state
      : { pending: false, error: null, completed: false }
  return {
    beginBinding: () => {
      if (userId && !active.current) {
        active.current = true
        start.mutate(userId)
      }
    },
    retryConsent: () => {
      const intentId = status.data?.pendingIntentId
      if (userId && intentId && !active.current)
        void wrap(finish({ accountId: userId, intentId }))
    },
    pending: relevant.pending,
    error: relevant.error ?? (status.isError ? status.error.message : null),
    statusFailed: status.isError,
    retryStatus: () => {
      void status.refetch()
    },
    completed: relevant.completed,
    recoverable: Boolean(
      status.data?.pendingIntentId && status.data.emailConnected,
    ),
  }
}
